import { useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import type { GroupTagUpdateItem } from '@trinity/chat';
import { Remirror as RemirrorTypes } from '@trinity/chat';
import { AudioPlayer } from '@trinity/chat-components/components/AudioPlayer';
import { BasicMarkupEditor } from '@trinity/chat-components/components/Remirror/BasicMarkupEditor';
import { VideoPlayer } from '@trinity/chat-components/components/VideoPlayer';
import { AudioPlayerContainer } from '@trinity/chat-components/containers/AudioPlayer';
import type { SetMediaRef } from './interfaces';

type Props = {
  title?: string;
  disableMediaControls?: boolean;
  insights: RemirrorTypes.RootNode;
  color?: string;
  showColorBand?: boolean;
  speaker?: {
    name: string;
    start: number;
    end: number;
  };
  media: {
    clipUrl?: string;
    type: 'video' | 'audio' | null;
    url: string | null;
  };
  link?: string;
  mediaSetting: 'video' | 'audio' | null;
  setMediaRef: SetMediaRef;
  tags?: GroupTagUpdateItem[];
  displayTags?: boolean;
  onClick?: () => void;
};

export const WidgetMediaCard = (props: Props) => {
  const {
    setMediaRef,
    media,
    mediaSetting,
    disableMediaControls,
    insights,
    displayTags,
    tags,
    title,
    color,
    showColorBand,
    onClick,
  } = props;

  const url = useMemo(() => {
    return media?.clipUrl || media?.url || null;
  }, [media]);

  const handleVideoRef = useCallback(
    (node: HTMLVideoElement | null) => {
      setMediaRef?.(node);
    },
    [setMediaRef]
  );

  const showMedia = !!media?.type && !!url && !!mediaSetting;
  const showInsights = !isContentEmpty(insights);
  const showTags = displayTags && tags && tags.length > 0;

  const audioClipRange = useMemo(() => {
    if (!Number.isFinite(props.speaker?.start) || !Number.isFinite(props.speaker?.end)) {
      return null;
    }

    const start = Math.max(0, Number(props.speaker?.start));
    const end = Math.max(0, Number(props.speaker?.end));

    if (end <= start) {
      return null;
    }

    return { start, end };
  }, [props.speaker?.end, props.speaker?.start]);

  return (
    <CardRoot onClick={onClick} clickable={!!onClick} color={color} showColorBand={showColorBand}>
      <CardInner showColorBand={showColorBand}>
        {title && <CardTitle>{title}</CardTitle>}

        {showMedia && (
          <div style={{ pointerEvents: disableMediaControls ? 'none' : undefined }}>
            {mediaSetting === 'video' && (
              <VideoSection>
                <VideoPlayer
                  ref={handleVideoRef}
                  controls={!disableMediaControls}
                  style={{ width: '100%', display: 'block' }}
                  src={url}
                />
                {disableMediaControls && (
                  <PlayOverlay>
                    <PlayCircle>
                      <PlayTriangle />
                    </PlayCircle>
                  </PlayOverlay>
                )}
              </VideoSection>
            )}
            {mediaSetting === 'audio' && (
              <AudioSection>
                <AudioPlayerContainer>
                  <AudioPlayer
                    audio={{ src: url }}
                    clipStart={audioClipRange?.start}
                    clipEnd={audioClipRange?.end}
                  />
                </AudioPlayerContainer>
              </AudioSection>
            )}
          </div>
        )}

        {showInsights && (
          <DescriptionSection>
            <BasicMarkupEditor
              key={JSON.stringify(insights)}
              editing={false}
              content={insights}
              imageToolbarOptions={{ videos: false }}
            />
          </DescriptionSection>
        )}

        {showTags && (
          <TagRow>
            {tags.map((tag) => (
              <TagPill key={tag.id} tagColor={color}>
                {tag.name}
              </TagPill>
            ))}
          </TagRow>
        )}
      </CardInner>
    </CardRoot>
  );
};

WidgetMediaCard.displayName = 'Widget.Media.Card';

const CardRoot = styled.div<{ clickable?: boolean; color?: string; showColorBand?: boolean }>`
  border-radius: 8px;
  background-color: ${({ color, showColorBand }) => (showColorBand && color ? color : 'var(--pri-01)')};
  display: flex;
  justify-content: flex-end;
  overflow: hidden;
  width: 100%;
  ${({ clickable }) =>
    clickable &&
    `
    cursor: pointer;
    &:hover {
      box-shadow: 0 2px 8px rgba(0,0,0,0.12);
    }
  `}
`;

const CardInner = styled.div<{ showColorBand?: boolean }>`
  background: var(--pri-01);
  width: ${({ showColorBand }) => (showColorBand ? 'calc(100% - 10px)' : '100%')};
  border-radius: ${({ showColorBand }) => (showColorBand ? '0 8px 8px 0' : '8px')};
  overflow: hidden;
`;

const CardTitle = styled.div`
  font-size: 15px;
  font-family: var(--font-bold);
  padding: 10px 12px 6px;
  color: var(--black);
`;

const VideoSection = styled.div`
  position: relative;
  width: 100%;
  background: #000;
  line-height: 0;
  aspect-ratio: 16 / 9;

  & video {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const AudioSection = styled.div`
  position: relative;
  width: 100%;
`;

const PlayOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlayCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlayTriangle = styled.div`
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 9px 0 9px 16px;
  border-color: transparent transparent transparent white;
  margin-left: 3px;
`;

const DescriptionSection = styled.div`
  padding: 0 12px;
  & [contenteditable='false'] {
    font-size: 14px;
    line-height: 20px;
    color: var(--gray-d);
  }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 12px 10px;
`;

const TagPill = styled.div<{ tagColor?: string }>`
  background-color: ${({ tagColor }) => tagColor || '#b4dff1'};
  border-radius: 20px;
  font-size: 12px;
  font-family: var(--font-semibold);
  padding: 3px 10px;
  color: var(--black);
`;

function isContentEmpty(value: RemirrorTypes.RootNode) {
  if (!value) return true;

  function isParagraphNodeEmpty(node: RemirrorTypes.ParagraphNode) {
    const content = node.content || [];
    if (!content.length) return true;
    return content.every(isParagraphContentEmpty);
  }

  return (value.content || []).reduce((acc, node) => {
    if (!acc) return acc;
    return isParagraphNodeEmpty(node);
  }, true);
}

function isParagraphContentEmpty(node: RemirrorTypes.TextNode) {
  if (node.type === RemirrorTypes.NodeType.Text) {
    return !node.text.trim().length;
  }
  return false;
}
